import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Modal,
  Grid,
  Column,
  Dropdown,
  Layer,
  TextArea,
  InlineNotification,
} from '@carbon/react';
import { Add, Information } from '@carbon/react/icons';
import { cidrIpRegexPattern, ipRangeRegexPattern } from '../../../lib/regex';
import {
  DeploymentEnvironment,
  DeploymentEnvironmentSubtype,
  ServiceEntryPoint,
} from '../../../models/master';

import './EditExternalClientAccess.scss';
import { Tooltip } from '@carbon/react';
import RegisterVPC from './RegisterVPC/RegisterVPC';
import { addCloudVpc } from '../../../controllers/cloudApis';
import { checkIpIsInRange } from '../../../lib/utils';

interface Data {
  name: string;
  cluster: DeploymentEnvironment | null;
  namespace: string;
  vpc: string;
  externalClientAccess: string;
  ipAddressPool: string;
  entryPoints: ServiceEntryPoint[];
}

const EditExternalClientAccess = (props: {
  data: Data;
  open: boolean;
  confirmSave: (payload: any) => void;
  closeModal: () => void;
  vpcList: DeploymentEnvironment[];
  deploymentEnvSubtypes: DeploymentEnvironmentSubtype[];
}) => {
  const {
    data,
    open,
    confirmSave,
    vpcList,
    deploymentEnvSubtypes,
    closeModal,
  } = props;
  const { t } = useTranslation('gatewayDetails');

  const [payload, setPayload] = useState<any>({
    external_client_access: data.externalClientAccess,
    ip_address_pool: data.ipAddressPool,
    vpc: null,
  });
  const [invalid, setInvalid] = useState(false);
  const [warningNotification, setWarningNotification] = useState({
    display: false,
    kind: 'disable',
    ipList: [] as ServiceEntryPoint[],
  });
  const [confirmModal, setConfirmModal] = useState(false);
  const [createVpcTearsheet, setCreateVpcTearsheet] = useState(false);

  const accessOptions = [
    {
      id: 'disabled',
      text: t('externalClientAccess.edit.disabled'),
    },
    {
      id: 'loadbalancer',
      text: t('externalClientAccess.edit.enabled'),
    },
  ];

  const handleIpPoolChange = (str: string) => {
    const originalEntryPoints = data.entryPoints;
    const newList = str.split(',').map((str: string) => str.trim());

    let removedEntryPoints: ServiceEntryPoint[] = [];

    originalEntryPoints.forEach((ep: ServiceEntryPoint) => {
      let foundValidRange = false;
      newList.forEach((range: string) => {
        if (!foundValidRange && checkIpIsInRange(ep.ip, range)) {
          foundValidRange = true;
        }
      });
      //only pushing here, once per original entry
      if (!foundValidRange) {
        removedEntryPoints.push(ep);
      }
    });

    if (removedEntryPoints.length > 0) {
      setWarningNotification({
        display: true,
        kind: 'remove',
        ipList: removedEntryPoints,
      });
    } else {
      if (warningNotification.display) {
        setWarningNotification((prev: any) => ({
          ...prev,
          display: false,
          ipList: [],
        }));
      }
    }

    setPayload((prev: any) => ({
      ...prev,
      ip_address_pool: str,
    }));
  };

  const validateForm = () => {
    const ipList = payload.ip_address_pool
      .split(',')
      .map((str: string) => str.trim());
    for (let i = 0; i < ipList.length; i++) {
      const ipRegex = cidrIpRegexPattern();
      if (!ipRegex.test(ipList[i])) {
        // check if hyphenated if fails
        const rangeRegex = ipRangeRegexPattern();
        if (!rangeRegex.test(ipList[i])) {
          setInvalid(true);
          return;
        }
      }
    }

    if (warningNotification.display) {
      setConfirmModal(true);
    } else {
      confirmSave(payload);
    }
  };

  {
    /*
  const handleCreateVpc = async (payload: any) => {
    try {
      if (data.cluster?.cloud_id) {
        let clusterType = data.cluster?.subtype;

        // get cloud_type_code for cluster
      }
      const newVpc = await addCloudVpc(data.cluster?.cloud_id, {});
    } catch (error) {
      console.log('error', error);
    }
  };
  */
  }
  return (
    <>
      <Modal
        className='edit-eac-modal'
        modalHeading={t('externalClientAccess.edit.modalHeading', {
          name: data.name,
        })}
        onRequestSubmit={validateForm}
        onRequestClose={() => closeModal()}
        open={open}
        preventCloseOnClickOutside
        primaryButtonText={t('externalClientAccess.edit.save')}
        size='md'
      >
        <div className='edit-eac-container'>
          <div className='description'>
            {t('externalClientAccess.edit.modalDescription')}
          </div>
          <div className='header'>
            {t('externalClientAccess.edit.deployedInEnv')}
          </div>
          <Grid className='grid'>
            <Column lg={5}>
              <div className='label'>
                {t('externalClientAccess.edit.namespace')}
              </div>
              <div className='value'>{data.namespace}</div>
            </Column>
            <Column lg={5}>
              <div className='label'>
                {t('externalClientAccess.edit.cluster')}
              </div>
              <div className='value'>{data.cluster?.name}</div>
            </Column>
            {data.externalClientAccess !== 'disabled' && (
              <Column lg={5}>
                <div className='label'>{t('vpc')}</div>
                <div className='value'>{data.vpc}</div>
              </Column>
            )}
          </Grid>
          <Grid className='grid'>
            <Column lg={9}>
              <Layer>
                <Dropdown
                  items={accessOptions}
                  selectedItem={
                    payload.external_client_access === 'disabled'
                      ? accessOptions[0]
                      : accessOptions[1]
                  }
                  onChange={(e: any) => {
                    if (
                      payload.external_client_access !== 'disabled' &&
                      e?.selectedItem.id === 'disabled'
                    ) {
                      setWarningNotification({
                        display: true,
                        kind: 'disable',
                        ipList: data.entryPoints,
                      });
                    }
                    setPayload((prev: any) => ({
                      ...prev,
                      external_client_access:
                        e?.selectedItem.id === 'disabled'
                          ? 'disabled'
                          : 'loadbalancer',
                    }));
                  }}
                  titleText={t('externalClientAccess.header')}
                  itemToString={(item: any) => item.text}
                />
              </Layer>
            </Column>
          </Grid>
          {payload.external_client_access !== 'disabled' && (
            <Layer>
              <TextArea
                labelText={t('externalClientAccess.edit.enterIpPool')}
                helperText={t('externalClientAccess.edit.ipPoolHelperText')}
                value={payload.ip_address_pool}
                onChange={(e: any) =>
                  handleIpPoolChange(e?.target?.value ?? '')
                }
                invalidText={t('externalClientAccess.edit.ipPoolInvalidText')}
                invalid={invalid}
              />
            </Layer>
          )}

          {warningNotification.display && (
            <InlineNotification
              className='se-warning-notification'
              hideCloseButton
              lowContrast
              kind='warning'
              title={t('externalClientAccess.edit.warningTitle')}
              subtitle={t(
                warningNotification.kind === 'disable'
                  ? 'externalClientAccess.edit.warningDisable'
                  : 'externalClientAccess.edit.warningRemove',
                {
                  count: data.entryPoints.length,
                }
              )}
            />
          )}
          {warningNotification.display && (
            <div className='service-entrypoints-list'>
              <div className='row'>
                <div className='table-header'>
                  {t('externalClientAccess.edit.serviceName')}
                </div>
                <div className='table-header'>
                  {t('externalClientAccess.edit.ipAddress')}
                </div>
              </div>

              {warningNotification.ipList.map(
                (entrypoint: ServiceEntryPoint) => (
                  <div className='row'>
                    <div className='cell'>{entrypoint.service_name}</div>
                    <div className='cell'>{entrypoint.entry_points}</div>
                  </div>
                )
              )}
            </div>
          )}
          {/*
          {payload.external_client_access !== 'disabled' &&
            data.externalClientAccess === 'disabled' && (
              <Grid className='grid vpc-dropdown'>
                <Column lg={9}>
                  <Layer>
                    <Dropdown
                      items={[
                        ...vpcList,
                        {
                          resource_id: 'add-vpc',
                        },
                      ]}
                      selectedItem={payload.vpc}
                      onChange={(e: any) => {
                        if (e?.selectedItem.resource_id === 'add-vpc') {
                          setCreateVpcTearsheet(true);
                        } else {
                          setPayload((prev: any) => ({
                            ...prev,
                            vpc: e?.selectedItem,
                          }));
                        }
                      }}
                      titleText={
                        <div className='label-with-tooltip'>
                          {t('externalClientAccess.edit.vpcDropdown.label')}
                          <Tooltip
                            label={t(
                              'externalClientAccess.edit.vpcDropdown.tooltip'
                            )}
                            align='bottom'
                          >
                            <Information size={16} />
                          </Tooltip>
                        </div>
                      }
                      label={t(
                        'externalClientAccess.edit.vpcDropdown.placeholder'
                      )}
                      itemToString={(item: any) =>
                        `${item.name}, ${item.cloud_name}, ${item.location_name}`
                      }
                      itemToElement={(item: any) => {
                        if (item.resource_id === 'add-vpc') {
                          return (
                            <div className='add-vpc-option'>
                              <span>
                                {t(
                                  'externalClientAccess.edit.vpcDropdown.create'
                                )}
                              </span>
                              <Add size={16} />
                            </div>
                          );
                        }
                        return (
                          <div className='vpc-dropdown-option'>
                            <span className='name'>{item.name}</span>
                            <span className='cloud-loc'>{`${item.cloud_name}, ${item.location_name}`}</span>
                          </div>
                        );
                      }}
                    />
                  </Layer>
                </Column>
              </Grid>
            )}
              */}
        </div>
      </Modal>
      {confirmModal && (
        <Modal
          className='confirm-eac-modal is-visible'
          modalHeading={
            warningNotification.kind === 'disable'
              ? t('externalClientAccess.edit.confirmModal.disableTitle')
              : t('externalClientAccess.edit.confirmModal.removeTitle')
          }
          primaryButtonText={t(
            'externalClientAccess.edit.confirmModal.confirm'
          )}
          secondaryButtonText={t(
            'externalClientAccess.edit.confirmModal.cancel'
          )}
          onRequestClose={() => setConfirmModal(false)}
          onRequestSubmit={() => {
            setConfirmModal(false);
            confirmSave(payload);
          }}
          danger
          size='sm'
        >
          <Trans
            i18nKey={t('externalClientAccess.edit.confirmModal.body', {
              count: warningNotification.ipList.length,
            })}
          />
        </Modal>
      )}
      {/*
      createVpcTearsheet && (
        <RegisterVPC
          open={createVpcTearsheet}
          cluster={data.cluster}
          handleSubmit={handleCreateVpc}
          closeTearsheet={() => setCreateVpcTearsheet(false)}
        />
      
      */}
    </>
  );
};

export default EditExternalClientAccess;
